import React from 'react';
import { cacheService } from './cacheService';

// Performance monitoring service
class PerformanceMonitoring {
  constructor() {
    this.metrics = {
      apiCalls: [],
      cacheHits: 0,
      cacheMisses: 0,
      averageResponseTime: 0,
      errorRate: 0,
      totalRequests: 0
    };
    this.thresholds = {
      slowQuery: 1000, // ms
      errorRate: 0.1, // 10%
      cacheHitRate: 0.8 // 80%
    };
    this.startTime = Date.now();
  }

  // Track API call performance
  trackAPICall(endpoint, duration, success, error = null) {
    const apiCall = {
      endpoint,
      duration,
      success,
      error: error?.message,
      timestamp: Date.now()
    };

    this.metrics.apiCalls.push(apiCall);
    this.metrics.totalRequests++;

    // Update averages
    this.updateAverages();
    
    // Check for performance issues
    this.checkPerformanceIssues(apiCall);
  }

  // Update performance averages
  updateAverages() {
    const successfulCalls = this.metrics.apiCalls.filter(call => call.success);
    
    if (successfulCalls.length > 0) {
      this.metrics.averageResponseTime = 
        successfulCalls.reduce((sum, call) => sum + call.duration, 0) / successfulCalls.length;
    }

    this.metrics.errorRate = 
      (this.metrics.apiCalls.length - successfulCalls.length) / this.metrics.apiCalls.length;
  }

  // Check for performance issues
  checkPerformanceIssues(apiCall) {
    const issues = [];

    if (apiCall.duration > this.thresholds.slowQuery) {
      issues.push({
        type: 'slow_query',
        endpoint: apiCall.endpoint,
        duration: apiCall.duration,
        threshold: this.thresholds.slowQuery
      });
    }

    if (!apiCall.success) {
      issues.push({
        type: 'api_error',
        endpoint: apiCall.endpoint,
        error: apiCall.error
      });
    }

    if (issues.length > 0) {
      this.reportIssues(issues);
    }
  }

  // Report performance issues
  reportIssues(issues) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Performance Issues Detected:', issues);
    }

    // Send to monitoring service (e.g., Sentry, DataDog)
    if (window.gtag) {
      issues.forEach(issue => {
        window.gtag('event', 'performance_issue', {
          issue_type: issue.type,
          endpoint: issue.endpoint,
          duration: issue.duration,
          error: issue.error
        });
      });
    }
  }

  // Get performance metrics
  getMetrics() {
    const uptime = Date.now() - this.startTime;
    const cacheStats = cacheService.getCacheStats();
    
    return {
      ...this.metrics,
      uptime,
      cacheStats,
      thresholds: this.thresholds,
      recommendations: this.getRecommendations()
    };
  }

  // Get performance recommendations
  getRecommendations() {
    const recommendations = [];

    if (this.metrics.averageResponseTime > this.thresholds.slowQuery) {
      recommendations.push({
        type: 'optimization',
        message: 'Consider implementing query optimization or caching for slow endpoints',
        priority: 'high'
      });
    }

    if (this.metrics.errorRate > this.thresholds.errorRate) {
      recommendations.push({
        type: 'reliability',
        message: 'High error rate detected. Consider implementing retry logic or error handling',
        priority: 'high'
      });
    }

    const cacheHitRate = this.metrics.cacheHits / (this.metrics.cacheHits + this.metrics.cacheMisses);
    if (cacheHitRate < this.thresholds.cacheHitRate) {
      recommendations.push({
        type: 'caching',
        message: 'Low cache hit rate. Consider optimizing cache strategy',
        priority: 'medium'
      });
    }

    return recommendations;
  }

  // Track cache performance
  trackCacheHit() {
    this.metrics.cacheHits++;
  }

  trackCacheMiss() {
    this.metrics.cacheMisses++;
  }

  // Reset metrics
  resetMetrics() {
    this.metrics = {
      apiCalls: [],
      cacheHits: 0,
      cacheMisses: 0,
      averageResponseTime: 0,
      errorRate: 0,
      totalRequests: 0
    };
    this.startTime = Date.now();
  }

  // Export metrics for external monitoring
  exportMetrics() {
    return {
      timestamp: new Date().toISOString(),
      metrics: this.getMetrics(),
      environment: process.env.NODE_ENV,
      userAgent: navigator.userAgent,
      url: window.location.href
    };
  }
}

// Export singleton instance
export const performanceMonitoring = new PerformanceMonitoring();

// Performance monitoring utilities
export const performanceUtils = {
  // Measure function execution time
  measureExecution: async (fn, label) => {
    const start = performance.now();
    try {
      const result = await fn();
      const duration = performance.now() - start;
      performanceMonitoring.trackAPICall(label, duration, true);
      return result;
    } catch (error) {
      const duration = performance.now() - start;
      performanceMonitoring.trackAPICall(label, duration, false, error);
      throw error;
    }
  },

  // Monitor component render performance
  monitorComponent: (Component) => {
    return class extends React.Component {
      componentDidMount() {
        this.renderStart = performance.now();
      }

      componentDidUpdate() {
        const renderTime = performance.now() - this.renderStart;
        performanceMonitoring.trackAPICall(
          `component_render:${Component.name}`,
          renderTime,
          true
        );
      }

      render() {
        return <Component {...this.props} />;
      }
    };
  },

  // Monitor network requests
  monitorNetwork: () => {
    const originalFetch = window.fetch;
    
    window.fetch = async (...args) => {
      const start = performance.now();
      try {
        const response = await originalFetch(...args);
        const duration = performance.now() - start;
        performanceMonitoring.trackAPICall(args[0], duration, response.ok);
        return response;
      } catch (error) {
        const duration = performance.now() - start;
        performanceMonitoring.trackAPICall(args[0], duration, false, error);
        throw error;
      }
    };
  }
};

export default performanceMonitoring;
